/**
 * Entrar e sair da sala de uma chamada.
 *
 * Só áudio: `audio: true, video: false`. Pedir vídeo faria o navegador
 * solicitar permissão de câmera numa ligação de voz — e uma permissão negada
 * costuma derrubar o pedido inteiro, junto com o microfone.
 */
import { useCallback } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { useCallStore } from "@/store/call";
import { voiceService } from "@/services/voice";

export function useLiveKitCall() {
  const store = useCallStore();

  /**
   * Entrar na sala com o token da chamada.
   *
   * Dois caminhos chegam aqui: atender uma ligação que entrou e discar uma
   * que sai. A diferença é só o que vem ANTES — na entrada é preciso ganhar a
   * corrida do aceite; na saída quem discou já é o dono. O que vem depois
   * (token, conectar, ligar o microfone) é idêntico, e escrever duas vezes
   * daria duas formas de entrar na sala que divergiriam na primeira mudança.
   */
  const entrarNaSala = useCallback(async (callId: string): Promise<boolean> => {
    const { conectada, falhou } = useCallStore.getState();

    try {
      const { token, url } = await voiceService.token(callId);

      const room = new Room({ adaptiveStream: false, dynacast: false });

      room.on(RoomEvent.Disconnected, () => {
        useCallStore.getState().encerrar();
      });

      // O áudio de quem ligou: sem anexar a faixa a um elemento, a conexão
      // existe e ninguém ouve nada.
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          el.setAttribute("data-voz", "remoto");
          document.body.appendChild(el);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => track.detach().forEach(
        (el) => el.remove(),
      ));

      await room.connect(url, token);
      await room.localParticipant.setMicrophoneEnabled(true);

      conectada(room);
      return true;
    } catch (e) {
      const msg = (e as { error?: { message?: string } })?.error?.message;
      falhou(msg || "Não consegui entrar na chamada.");
      return false;
    }
  }, []);

  const atender = useCallback(async (callId: string): Promise<boolean> => {
    const { conectando, falhou } = useCallStore.getState();
    conectando(callId);
    try {
      // Primeiro a corrida: quem chega antes leva. Só depois vale pedir o
      // token — perdendo a corrida, não há sala para entrar.
      await voiceService.accept(callId);
    } catch (e) {
      const msg = (e as { error?: { message?: string } })?.error?.message;
      falhou(msg || "Não consegui entrar na chamada.");
      return false;
    }
    return entrarNaSala(callId);
  }, [entrarNaSala]);

  /**
   * Ligar para o contato e entrar na linha.
   *
   * **Entrar é obrigatório.** Sem isto a tela fica em "conectando" para
   * sempre: o celular do cliente toca, ele atende, e quem discou não está na
   * sala — ouve silêncio enquanto o cliente fala sozinho.
   */
  const discar = useCallback(async (contactId: string): Promise<boolean> => {
    const { conectando, falhou } = useCallStore.getState();
    let callId: string;
    try {
      const chamada = await voiceService.dial(contactId);
      callId = chamada.id;
      conectando(callId);
    } catch (e) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data;
      falhou(d?.detail || "Não consegui completar a ligação.");
      return false;
    }
    return entrarNaSala(callId);
  }, [entrarNaSala]);

  const recusar = useCallback(async (callId: string) => {
    useCallStore.getState().pararDeTocar(callId);
    await voiceService.decline(callId).catch(() => undefined);
  }, []);

  const desligar = useCallback(async () => {
    const { callId, encerrar } = useCallStore.getState();
    encerrar();
    if (callId) await voiceService.hangup(callId).catch(() => undefined);
  }, []);

  const alternarMudo = useCallback(async () => {
    const { room, mudo, setMudo } = useCallStore.getState();
    if (!room) return;
    await room.localParticipant.setMicrophoneEnabled(mudo);
    setMudo(!mudo);
  }, []);

  return { ...store, atender, discar, recusar, desligar, alternarMudo };
}

export default useLiveKitCall;
